import React from 'react';
import { Check, X } from 'lucide-react';

/**
 * Componente para mostrar la validación en tiempo real de la fortaleza de la contraseña
 * según las políticas de seguridad definidas
 */
export const PasswordStrengthIndicator = ({ password, email = '' }) => {
  const checks = [
    { 
      test: password.length >= 8, 
      label: 'Mínimo 8 caracteres',
      id: 'length'
    },
    { 
      test: /[A-Z]/.test(password), 
      label: 'Una letra mayúscula (A-Z)',
      id: 'uppercase'
    },
    { 
      test: /[a-z]/.test(password), 
      label: 'Una letra minúscula (a-z)',
      id: 'lowercase'
    },
    { 
      test: /[0-9]/.test(password), 
      label: 'Un número (0-9)',
      id: 'number'
    },
    { 
      test: /[@#$%^&+=!*()_\-]/.test(password), 
      label: 'Un carácter especial (@#$%^&+=!*()_-)',
      id: 'special'
    },
    { 
      test: email ? !password.toLowerCase().includes(email.split('@')[0].toLowerCase()) : true, 
      label: 'No contiene tu correo electrónico',
      id: 'email'
    }
  ];

  // Calcular fortaleza general (porcentaje)
  const passedChecks = checks.filter(check => check.test).length;
  const strength = (passedChecks / checks.length) * 100;

  // Determinar color y texto según fortaleza
  const getStrengthInfo = () => {
    if (strength === 0) return { color: 'text-gray-400', bg: 'bg-gray-200', text: '' };
    if (strength < 50) return { color: 'text-red-600', bg: 'bg-red-500', text: 'Débil' };
    if (strength < 83) return { color: 'text-yellow-600', bg: 'bg-yellow-500', text: 'Media' };
    return { color: 'text-green-600', bg: 'bg-green-500', text: 'Fuerte' };
  };

  const strengthInfo = getStrengthInfo();

  return (
    <div className="space-y-3">
      {/* Barra de progreso de fortaleza */}
      {password && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-600">Fortaleza de la contraseña:</span>
            <span className={`font-semibold ${strengthInfo.color}`}>
              {strengthInfo.text}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div 
              className={`h-full transition-all duration-300 ${strengthInfo.bg}`}
              style={{ width: `${strength}%` }}
            />
          </div>
        </div>
      )}

      {/* Lista de requisitos */}
      <div className="space-y-1.5">
        <p className="text-xs font-medium text-gray-700 mb-2">
          Requisitos de seguridad:
        </p>
        {checks.map((check) => (
          <div 
            key={check.id} 
            className="flex items-start gap-2 text-xs transition-colors duration-200"
          >
            {check.test ? (
              <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
            ) : (
              <X className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
            )}
            <span className={check.test ? 'text-green-700 font-medium' : 'text-gray-600'}>
              {check.label}
            </span>
          </div>
        ))}
      </div>

      {/* Mensaje de ayuda adicional */}
      {password && strength < 100 && (
        <div className="mt-2 p-2 bg-blue-50 rounded-md border border-blue-200">
          <p className="text-xs text-blue-800">
            💡 <strong>Consejo:</strong> Usa una combinación de letras, números y símbolos para mayor seguridad.
          </p>
        </div>
      )}
    </div>
  );
};

export default PasswordStrengthIndicator;
